import type { Chart, ChartData, ChartOptions } from 'chart.js';
import React, { useMemo, useRef } from 'react';

import chartUtils, { formatDisplayLabel } from '@/features/(charts)/chart-renderer/lib/chart-utils';
import type { DashboardCardInfoT } from '@/features/Dashboard/types';
import useColor from '@/hooks/domain/useColor';
import { LineChartCanvas, useChartTooltip } from '@/shared/ui/charts';
import { getTimezoneDayjs } from '@/utils/time';

import ChartCustomTooltip from '../chart-custom-tooltip/ChartCustomTooltip';
import useChartTimeSeriesData from './useChartTimeSeriesData';

type Props = {
  chart: ChartBase & { name: string };
  tableData: ChartTableRes;
  dashboardCardInfo?: DashboardCardInfoT;
  yAxisTitle?: string;
};

const StackedAreaChart = (props: Props) => {
  const color = useColor();
  const chartRef = useRef<Chart>(null);

  const { tooltipData, chartTooltip } = useChartTooltip({
    chart: chartRef.current,
  });

  const areaChartData = useChartTimeSeriesData(props);

  const dateLabels = useMemo(() => {
    if (!props.chart.target.term.unit) throw new Error('Invalid frequency');
    const termRange = chartUtils.getTimeRangeByTerm(
      getTimezoneDayjs(),
      props.chart.target.term,
    );
    return chartUtils.getDateLabelsByUnit({
      dateRange: termRange,
      unit: props.chart.target.term.unit,
    });
  }, [props.chart.target.term]);

  const compareDateLabels = useMemo(() => {
    const compareRange = chartUtils.getCompareTimeRangeByTerm(
      getTimezoneDayjs(),
      props.chart.target.term,
      props.chart.target.compare_term,
    );
    return compareRange
      ? chartUtils.getDateLabelsByUnit({
          dateRange: compareRange,
          unit: props.chart.target.term.unit,
        })
      : null;
  }, [props.chart.target.compare_term, props.chart.target.term]);

  const areaChartOptions: ChartOptions<'line'> = useMemo(() => {
    return {
      scales: {
        y: {
          min: props.chart.series[0]?.options?.scale_min || 0,
          max: props.chart.series[0]?.options?.scale_max,
          grace: '15%',
          stacked: true,
          title: {
            display: !!props.yAxisTitle,
            text: props.yAxisTitle || '',
          },
        },
        x: {
          display: true,
          position: 'bottom',
          labels: dateLabels,
          ticks: {
            callback: function (value, index) {
              const rawLabel = this.getLabelForValue(value as number);
              const frequency = chartUtils.getFrequencyByTermUnit(
                props.chart.target.term.unit,
              );
              const prevLabel = index > 0 ? dateLabels[index - 1] : null;
              return formatDisplayLabel(rawLabel, frequency, prevLabel);
            },
          },
        },
        x1: {
          display: props.chart.target.is_compare,
          position: 'top',
          labels: compareDateLabels,
          ticks: {
            callback: function (value, index) {
              if (!compareDateLabels) return '';
              const rawLabel = this.getLabelForValue(value as number);
              const frequency = chartUtils.getFrequencyByTermUnit(
                props.chart.target.term.unit,
              );
              const prevLabel =
                index > 0 ? compareDateLabels[index - 1] : null;
              return formatDisplayLabel(rawLabel, frequency, prevLabel);
            },
          },
        },
      },
      plugins: {
        eventPlugin: {
          events: ['mouseleave', 'click'],
        },
        tooltip: {
          enabled: false,
          external: chartTooltip,
        },
        filler: {
          propagate: false,
        },
        legend: {
          labels: {
            color: color.gray700,
          },
          position: 'right',
        },
        datalabels: {
          display: false,
        },
      },
    };
  }, [
    chartTooltip,
    color.gray700,
    compareDateLabels,
    dateLabels,
    props.chart.series,
    props.chart.target.is_compare,
    props.chart.target.term.unit,
  ]);

  return (
    <React.Fragment>
      <LineChartCanvas
        data={areaChartData as ChartData<'line'>}
        options={areaChartOptions}
        chartRef={chartRef as React.RefObject<Chart<'line'>>}
      />
      {tooltipData && (
        <ChartCustomTooltip
          tooltip={tooltipData}
          meta={props.chart}
          tableData={props.tableData}
          dashboardCardInfo={props.dashboardCardInfo}
        />
      )}
    </React.Fragment>
  );
};

export default StackedAreaChart;
